import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Phone,
  Route,
  Server,
  Hash,
  Users,
  Info,
  ChevronUp,
  ChevronDown,
  Pencil,
  Trash2,
  Plus,
  Play,
  Loader2,
} from 'lucide-react';
import { AdminService } from '../../services/adminService';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env';

const ROTATION_MODES = [
  { id: 'fixed', label: 'Fixed', hint: 'Lowest priority number first' },
  { id: 'round_robin', label: 'Round robin', hint: 'Least recently used' },
  { id: 'random', label: 'Random', hint: 'Any active DID' },
];

const CallProviderSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [dids, setDids] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [telecallers, setTelecallers] = useState([]);

  const [providerModal, setProviderModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [form, setForm] = useState({});

  const [didModal, setDidModal] = useState(false);
  const [editingDid, setEditingDid] = useState(null);
  const [didForm, setDidForm] = useState({});

  const smartfloProvider = providers.find((p) => p.name === 'smartflo');

  // Test call state
  const [testMode, setTestMode] = useState('gateway'); // 'gateway' | provider name
  const [testFrom, setTestFrom] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, status, ms, body }

  const fireTestCall = async () => {
    const from10 = testFrom.replace(/\D/g, '').slice(-10);
    const to10 = testTo.replace(/\D/g, '').slice(-10);
    if (from10.length !== 10 || to10.length !== 10) {
      alert('Enter valid 10-digit from & to numbers');
      return;
    }
    setTestBusy(true);
    setTestResult(null);
    const t0 = Date.now();
    try {
      const base = `${SUPABASE_URL.replace(/\/+$/, '')}/functions/v1`;
      const headers = {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      };
      let url = `${base}/click-to-call-gateway`;
      const payload = { from: from10, to: to10 };
      if (testMode !== 'gateway') {
        payload.provider = testMode;
      }
      const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
      const body = await resp.json().catch(() => ({}));
      setTestResult({ ok: resp.ok && body?.success, status: resp.status, ms: Date.now() - t0, body });
    } catch (e) {
      setTestResult({ ok: false, status: 0, ms: Date.now() - t0, body: { error: e.message } });
    } finally {
      setTestBusy(false);
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [plist, c, usersRaw] = await Promise.all([
        AdminService.getCallProviders(),
        AdminService.getCallProviderConfig(),
        AdminService.getAllUsers(),
      ]);
      setProviders(plist);
      setCfg(c || { id: 1, fallback_enabled: true, smartflo_did_rotation_mode: 'fixed' });
      const sf = plist.find((p) => p.name === 'smartflo');
      if (sf) setDids(await AdminService.getProviderDids(sf.id));
      else setDids([]);
      setAssignments(await AdminService.getAgentDidAssignments());
      const tcs = (usersRaw || []).filter(
        (u) => Array.isArray(u.roles) && u.roles.some((r) => String(r).toLowerCase() === 'telecaller'),
      );
      setTelecallers(
        tcs.map((u) => ({
          id: u.id,
          name: u.name || u.full_name,
          phone: u.phone || u.contact_number || '',
          roles: u.roles,
        })),
      );
    } catch (e) {
      console.error(e);
      alert('Failed to load call provider settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const toggleProvider = async (p, enabled) => {
    const r = await AdminService.upsertCallProvider({ ...p, is_enabled: enabled });
    if (!r.ok) alert(r.error || 'Update failed');
    await loadAll();
  };

  const movePriority = async (p, dir) => {
    const sorted = [...providers].sort((a, b) => a.priority - b.priority);
    const idx = sorted.findIndex((x) => x.id === p.id);
    const swapWith = dir === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swapWith];
    await AdminService.updateCallProviderPriority(a.id, b.priority);
    await AdminService.updateCallProviderPriority(b.id, a.priority);
    await loadAll();
  };

  const openNewProvider = () => {
    setEditingProvider(null);
    setForm({
      name: '',
      display_name: '',
      api_base_url: 'https://',
      api_endpoint: '/v1/example',
      http_method: 'POST',
      auth_type: 'bearer',
      request_mapping: '{"from_field":"from","to_field":"to","params_in":"body","extra":{}}',
      auth_credentials: '{"token_env":"MY_TOKEN_ENV"}',
      has_did_support: false,
      is_enabled: true,
      priority: String(Math.max(...providers.map((x) => x.priority), 0) + 1),
    });
    setProviderModal(true);
  };

  const openEditProvider = (p) => {
    setEditingProvider(p);
    setForm({
      name: p.name,
      display_name: p.display_name,
      api_base_url: p.api_base_url,
      api_endpoint: p.api_endpoint,
      http_method: p.http_method,
      auth_type: p.auth_type,
      request_mapping: JSON.stringify(p.request_mapping || {}, null, 2),
      auth_credentials: JSON.stringify(p.auth_credentials || {}, null, 2),
      has_did_support: p.has_did_support,
      is_enabled: p.is_enabled,
      priority: String(p.priority),
    });
    setProviderModal(true);
  };

  const saveProvider = async () => {
    try {
      const mapping = JSON.parse(form.request_mapping || '{}');
      const creds = JSON.parse(form.auth_credentials || '{}');
      const priority = parseInt(form.priority, 10) || 10;
      if (!form.name?.trim() || !form.display_name?.trim()) {
        alert('Name and display name required');
        return;
      }
      const r = await AdminService.upsertCallProvider({
        id: editingProvider?.id,
        name: form.name.trim().toLowerCase().replace(/\s+/g, '_'),
        display_name: form.display_name.trim(),
        api_base_url: form.api_base_url.trim(),
        api_endpoint: form.api_endpoint.trim(),
        http_method: form.http_method,
        auth_type: form.auth_type,
        request_mapping: mapping,
        auth_credentials: creds,
        has_did_support: form.has_did_support,
        is_enabled: form.is_enabled,
        priority,
      });
      if (!r.ok) {
        alert(r.error || 'Save failed');
        return;
      }
      setProviderModal(false);
      await loadAll();
    } catch (e) {
      alert(e?.message || 'Invalid JSON');
    }
  };

  const removeProvider = (p) => {
    if (!window.confirm(`Delete ${p.display_name}?`)) return;
    AdminService.deleteCallProvider(p.id).then((r) => {
      if (!r.ok) alert(r.error);
      else loadAll();
    });
  };

  const saveCfg = async (patch) => {
    const r = await AdminService.updateCallProviderConfig(patch);
    if (!r.ok) alert(r.error);
    await loadAll();
  };

  const openNewDid = () => {
    if (!smartfloProvider) {
      alert('SmartFlo provider row missing');
      return;
    }
    setEditingDid(null);
    setDidForm({
      provider_id: smartfloProvider.id,
      did_number: '',
      label: '',
      priority: '1',
      is_active: true,
    });
    setDidModal(true);
  };

  const openEditDid = (d) => {
    setEditingDid(d);
    setDidForm({
      provider_id: d.provider_id,
      did_number: d.did_number,
      label: d.label || '',
      priority: String(d.priority),
      is_active: d.is_active,
    });
    setDidModal(true);
  };

  const saveDid = async () => {
    if (!didForm.did_number?.trim()) {
      alert('DID required');
      return;
    }
    const r = await AdminService.upsertDid({
      id: editingDid?.id,
      provider_id: didForm.provider_id,
      did_number: didForm.did_number.trim(),
      label: didForm.label?.trim() || null,
      priority: parseInt(didForm.priority, 10) || 1,
      is_active: didForm.is_active,
    });
    if (!r.ok) alert(r.error);
    else {
      setDidModal(false);
      await loadAll();
    }
  };

  const setAssignmentDid = async (phone, name, didId) => {
    const r = await AdminService.upsertAgentDidAssignment({
      agent_phone: phone,
      agent_name: name || null,
      did_id: didId,
      is_active: true,
    });
    if (!r.ok) alert(r.error);
    await loadAll();
  };

  const sortedProviders = [...providers].sort((a, b) => a.priority - b.priority);

  if (loading && !providers.length) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <RefreshCw className="animate-spin text-cyan-600" size={36} strokeWidth={2} />
        <p className="text-sm text-slate-500 font-medium">Loading telephony configuration…</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-slate-200/80 pb-6">
        <div className="flex items-start gap-4">
          <button
            type="button"
            onClick={() => navigate('/more')}
            className="mt-1 p-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
            aria-label="Back to More"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Call provider & DID</h1>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-cyan-50 text-cyan-800 border border-cyan-100">
                Admin
              </span>
            </div>
            <p className="text-slate-600 mt-1.5 text-sm sm:text-base max-w-2xl">
              Control outbound click-to-call routing, SmartFlo caller IDs, and per-agent overrides. Telecallers still see a single call button in the app.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={loadAll}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold shadow-sm hover:bg-slate-50 transition-colors shrink-0"
        >
          <RefreshCw size={17} />
          Refresh
        </button>
      </div>

      {/* Info strip */}
      <div className="flex gap-3 rounded-xl border border-amber-200/80 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
        <Info className="shrink-0 text-amber-600 mt-0.5" size={18} />
        <p>
          <span className="font-semibold text-amber-900">Secrets stay in Supabase.</span>{' '}
          Sarv (<code className="text-xs bg-amber-100/80 px-1 rounded">SARV_USER_ID</code>,{' '}
          <code className="text-xs bg-amber-100/80 px-1 rounded">SARV_TOKEN</code>) and SmartFlo tokens belong in{' '}
          <strong>Edge Functions → Secrets</strong>, not in this form—unless you add a custom provider with env-based JSON.
        </p>
      </div>

      {/* Global routing */}
      <section className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 bg-slate-50/80">
          <Route className="text-cyan-600" size={20} />
          <h2 className="text-base font-semibold text-slate-900">Global routing</h2>
        </div>
        <div className="p-5 space-y-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="max-w-xl">
              <p className="text-sm font-medium text-slate-900">Fallback chain</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, if the first provider fails (busy, error, or no channel), the gateway tries the next enabled provider by priority.
              </p>
            </div>
            <label className="inline-flex items-center gap-3 cursor-pointer select-none shrink-0">
              <span className="text-sm font-medium text-slate-700">Enable fallback</span>
              <input
                type="checkbox"
                checked={cfg?.fallback_enabled !== false}
                onChange={(e) => saveCfg({ fallback_enabled: e.target.checked })}
                className="sr-only peer"
              />
              <span className="relative w-11 h-6 bg-slate-200 peer-focus-visible:ring-2 peer-focus-visible:ring-cyan-500 peer-focus-visible:ring-offset-2 rounded-full transition-colors peer-checked:bg-cyan-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-5" />
            </label>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <div className="mb-3">
              <p className="text-sm font-medium text-slate-900 flex items-center gap-2">
                <Hash size={16} className="text-slate-400" />
                DID rotation strategy
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Applies to providers with DID support (e.g. SmartFlo). Sarv does not use a DID pool.
              </p>
            </div>
            <div className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200/80">
              {ROTATION_MODES.map((m) => {
                const active = cfg?.smartflo_did_rotation_mode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => saveCfg({ smartflo_did_rotation_mode: m.id })}
                    title={m.hint}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      active
                        ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {ROTATION_MODES.find((x) => x.id === cfg?.smartflo_did_rotation_mode)?.hint}
            </p>
          </div>
        </div>
      </section>

      {/* Providers */}
      <section className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/80">
          <div className="flex items-center gap-2">
            <Server className="text-cyan-600" size={20} />
            <div>
              <h2 className="text-base font-semibold text-slate-900">Providers</h2>
              <p className="text-xs text-slate-500">{sortedProviders.length} configured · lower priority number = tried first</p>
            </div>
          </div>
          <button
            type="button"
            onClick={openNewProvider}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-cyan-600 text-white text-sm font-semibold shadow-sm hover:bg-cyan-700 transition-colors"
          >
            <Plus size={18} />
            Add provider
          </button>
        </div>

        <div className="p-5">
          {sortedProviders.length === 0 ? (
            <div className="text-center py-14 px-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/50">
              <Server className="mx-auto text-slate-300 mb-3" size={40} />
              <p className="text-slate-700 font-medium">No providers loaded</p>
              <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                Run the database migration for <code className="text-xs bg-white px-1.5 py-0.5 rounded border">call_providers</code> or check that your admin user can read this table (RLS). Then refresh, or add a provider manually.
              </p>
              <button
                type="button"
                onClick={openNewProvider}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 hover:bg-cyan-100"
              >
                <Plus size={16} />
                Add provider
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="px-4 py-3 w-16">#</th>
                    <th className="px-4 py-3">Provider</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Endpoint</th>
                    <th className="px-4 py-3">DID</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedProviders.map((p, i, arr) => (
                    <tr key={p.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-3 align-middle">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-700 font-mono text-xs font-semibold">
                          {p.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="font-semibold text-slate-900">{p.display_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5 font-mono">{p.name}</div>
                      </td>
                      <td className="px-4 py-3 align-middle hidden lg:table-cell">
                        <div className="text-xs text-slate-600 font-mono break-all max-w-xs">
                          {p.http_method} {p.api_base_url}
                          {p.api_endpoint}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                            p.has_did_support ? 'bg-violet-50 text-violet-800' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {p.has_did_support ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={p.is_enabled}
                            onChange={(e) => toggleProvider(p, e.target.checked)}
                            className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                          />
                          <span className="text-slate-700">{p.is_enabled ? 'Enabled' : 'Disabled'}</span>
                        </label>
                      </td>
                      <td className="px-4 py-3 align-middle text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <button
                            type="button"
                            disabled={i === 0}
                            onClick={() => movePriority(p, 'up')}
                            className="p-1.5 rounded-lg border border-transparent hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600"
                            title="Higher priority"
                          >
                            <ChevronUp size={18} />
                          </button>
                          <button
                            type="button"
                            disabled={i === arr.length - 1}
                            onClick={() => movePriority(p, 'down')}
                            className="p-1.5 rounded-lg border border-transparent hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600"
                            title="Lower priority"
                          >
                            <ChevronDown size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditProvider(p)}
                            className="p-1.5 rounded-lg text-cyan-700 hover:bg-cyan-50"
                            title="Edit"
                          >
                            <Pencil size={17} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeProvider(p)}
                            className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* SmartFlo DIDs */}
      {smartfloProvider && (
        <section className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/80">
            <div className="flex items-center gap-2">
              <Phone className="text-cyan-600" size={20} />
              <div>
                <h2 className="text-base font-semibold text-slate-900">DID numbers · SmartFlo</h2>
                <p className="text-xs text-slate-500">Caller IDs shown to customers when using SmartFlo</p>
              </div>
            </div>
            <button
              type="button"
              onClick={openNewDid}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-cyan-600 text-white text-sm font-semibold shadow-sm hover:bg-cyan-700"
            >
              <Plus size={18} />
              Add DID
            </button>
          </div>
          <div className="p-5">
            {dids.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8 border border-dashed rounded-xl border-slate-200">
                No DIDs yet. Add at least one active DID before enabling SmartFlo for production traffic.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                      <th className="px-4 py-3">Number</th>
                      <th className="px-4 py-3">Label</th>
                      <th className="px-4 py-3">Priority</th>
                      <th className="px-4 py-3 hidden md:table-cell">Usage</th>
                      <th className="px-4 py-3">Active</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dids.map((d) => (
                      <tr key={d.id} className="hover:bg-slate-50/80">
                        <td className="px-4 py-3 font-mono font-medium text-slate-900">{d.did_number}</td>
                        <td className="px-4 py-3 text-slate-600">{d.label || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{d.priority}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs hidden md:table-cell">
                          {d.total_calls ?? 0} calls
                          {d.last_used_at && (
                            <span className="block text-slate-400 mt-0.5">
                              Last {new Date(d.last_used_at).toLocaleString()}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={d.is_active}
                            onChange={(e) => AdminService.upsertDid({ ...d, is_active: e.target.checked }).then(() => loadAll())}
                            className="rounded border-slate-300 text-cyan-600"
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" className="text-cyan-700 hover:underline text-xs font-medium mr-3" onClick={() => openEditDid(d)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-red-600 hover:underline text-xs font-medium"
                            onClick={() => {
                              if (window.confirm('Delete this DID?')) AdminService.deleteDid(d.id).then(() => loadAll());
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Agent assignment */}
      <section className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 bg-slate-50/80">
          <Users className="text-cyan-600" size={20} />
          <div>
            <h2 className="text-base font-semibold text-slate-900">Agent DID assignment</h2>
            <p className="text-xs text-slate-500">Match telecaller &quot;from&quot; number (last 10 digits). Default pool uses rotation rules above.</p>
          </div>
        </div>
        <div className="p-5 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="pb-3 pr-4">Telecaller</th>
                <th className="pb-3 pr-4">Phone (10-digit match)</th>
                <th className="pb-3">Outbound caller ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {telecallers.map((tc) => {
                const phone = String(tc.phone || '').replace(/\D/g, '').slice(-10);
                const asn = assignments.find((a) => a.agent_phone === phone);
                if (!phone || phone.length < 10) {
                  return (
                    <tr key={tc.id}>
                      <td className="py-3 pr-4 font-medium text-slate-900">{tc.name || '—'}</td>
                      <td colSpan={2} className="py-3 text-amber-700 text-xs">
                        Invalid or missing phone on user record — update user profile first.
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={tc.id} className="align-top">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-900">{tc.name}</div>
                    </td>
                    <td className="py-3 pr-4 font-mono text-slate-600">{tc.phone}</td>
                    <td className="py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setAssignmentDid(phone, tc.name, null)}
                          className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                            !asn?.did_id
                              ? 'border-cyan-500 bg-cyan-50 text-cyan-900'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          Default pool
                        </button>
                        {dids
                          .filter((d) => d.is_active)
                          .map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              onClick={() => setAssignmentDid(phone, tc.name, d.id)}
                              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-mono font-medium border transition-colors ${
                                asn?.did_id === d.id
                                  ? 'border-cyan-500 bg-cyan-50 text-cyan-900'
                                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                              }`}
                            >
                              {d.did_number}
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {telecallers.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">No telecaller users found.</p>
          )}
        </div>
      </section>

      {/* Test call */}
      <section className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 bg-slate-50/80">
          <Play className="text-cyan-600" size={20} />
          <div>
            <h2 className="text-base font-semibold text-slate-900">Test call</h2>
            <p className="text-xs text-slate-500">Pick a provider (or gateway auto-routing), enter numbers, and fire a live test.</p>
          </div>
        </div>
        <div className="p-5 space-y-5">
          {/* Route selector */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Route through</label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setTestMode('gateway')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  testMode === 'gateway'
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                Gateway (auto priority)
              </button>
              {providers.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setTestMode(p.name)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    testMode === p.name
                      ? 'bg-cyan-600 text-white border-cyan-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {p.display_name}
                  {!p.is_enabled && <span className="ml-1 text-xs opacity-60">(off)</span>}
                </button>
              ))}
            </div>
          </div>

          {/* From / To */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">From (agent number)</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 font-mono text-sm focus:ring-2 focus:ring-cyan-200 focus:border-cyan-400 outline-none"
                placeholder="e.g. 9876543210"
                value={testFrom}
                onChange={(e) => setTestFrom(e.target.value)}
                maxLength={15}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">To (customer number)</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 font-mono text-sm focus:ring-2 focus:ring-cyan-200 focus:border-cyan-400 outline-none"
                placeholder="e.g. 9123456789"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                maxLength={15}
              />
            </div>
          </div>

          {/* Fire button */}
          <button
            type="button"
            disabled={testBusy}
            onClick={fireTestCall}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {testBusy ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />}
            {testBusy ? 'Calling…' : 'Fire test call'}
          </button>

          {/* Result */}
          {testResult && (
            <div
              className={`rounded-xl border p-4 ${
                testResult.ok
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                    testResult.ok
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {testResult.ok ? 'Success' : 'Failed'}
                </span>
                <span className="text-xs text-slate-500">
                  HTTP {testResult.status} · {testResult.ms}ms
                </span>
                {testResult.body?.provider_used && (
                  <span className="text-xs font-semibold text-slate-700">
                    Provider: {testResult.body.provider_used}
                  </span>
                )}
              </div>
              <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-white/60 rounded-lg p-3 border border-slate-100 max-h-64 overflow-auto">
                {JSON.stringify(testResult.body, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </section>

      {/* Provider modal */}
      {providerModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-[2px]"
          onClick={() => setProviderModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/80 rounded-t-2xl">
              <h3 className="text-lg font-bold text-slate-900">{editingProvider ? 'Edit provider' : 'New provider'}</h3>
              <p className="text-xs text-slate-500 mt-1">Advanced JSON for custom integrations only.</p>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Internal name</label>
                <input
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500"
                  value={form.name}
                  disabled={!!editingProvider}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Display name</label>
                <input
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">API base URL</label>
                <input
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 font-mono text-xs"
                  value={form.api_base_url}
                  onChange={(e) => setForm({ ...form, api_base_url: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Endpoint</label>
                <input
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 font-mono text-xs"
                  value={form.api_endpoint}
                  onChange={(e) => setForm({ ...form, api_endpoint: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Method</label>
                  <input
                    className="w-full border border-slate-200 rounded-lg px-3 py-2"
                    value={form.http_method}
                    onChange={(e) => setForm({ ...form, http_method: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Auth type</label>
                  <input
                    className="w-full border border-slate-200 rounded-lg px-3 py-2"
                    value={form.auth_type}
                    onChange={(e) => setForm({ ...form, auth_type: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Priority</label>
                <input
                  className="w-full border border-slate-200 rounded-lg px-3 py-2"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-cyan-600"
                  checked={form.has_did_support}
                  onChange={(e) => setForm({ ...form, has_did_support: e.target.checked })}
                />
                <span className="text-slate-700">DID support</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-cyan-600"
                  checked={form.is_enabled}
                  onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
                />
                <span className="text-slate-700">Enabled</span>
              </label>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">request_mapping (JSON)</label>
                <textarea
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 font-mono text-xs"
                  rows={5}
                  value={form.request_mapping}
                  onChange={(e) => setForm({ ...form, request_mapping: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">auth_credentials (JSON)</label>
                <textarea
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 font-mono text-xs"
                  rows={4}
                  value={form.auth_credentials}
                  onChange={(e) => setForm({ ...form, auth_credentials: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-white"
                onClick={() => setProviderModal(false)}
              >
                Cancel
              </button>
              <button type="button" className="px-4 py-2 rounded-lg bg-cyan-600 text-white font-semibold hover:bg-cyan-700" onClick={saveProvider}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DID modal */}
      {didModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-[2px]"
          onClick={() => setDidModal(false)}
        >
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-200" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/80 rounded-t-2xl">
              <h3 className="text-lg font-bold text-slate-900">{editingDid ? 'Edit DID' : 'Add DID'}</h3>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">DID number</label>
                <input
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 font-mono"
                  value={didForm.did_number}
                  onChange={(e) => setDidForm({ ...didForm, did_number: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Label</label>
                <input
                  className="w-full border border-slate-200 rounded-lg px-3 py-2"
                  value={didForm.label}
                  onChange={(e) => setDidForm({ ...didForm, label: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Priority</label>
                <input
                  className="w-full border border-slate-200 rounded-lg px-3 py-2"
                  value={didForm.priority}
                  onChange={(e) => setDidForm({ ...didForm, priority: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-cyan-600"
                  checked={didForm.is_active}
                  onChange={(e) => setDidForm({ ...didForm, is_active: e.target.checked })}
                />
                <span className="text-slate-700">Active</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-medium"
                onClick={() => setDidModal(false)}
              >
                Cancel
              </button>
              <button type="button" className="px-4 py-2 rounded-lg bg-cyan-600 text-white font-semibold hover:bg-cyan-700" onClick={saveDid}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallProviderSettings;
